// Type definitions
export interface Position {
  x: number;
  y: number;
}

export interface GameConfig {
  readonly PLAYER_CHARACTER: string;
  readonly WALL_CHARACTER: string;
  readonly EMPTY_CHARACTER: string;
  readonly TREE_CHARACTER: string;
  readonly ROCK_CHARACTER: string;
  readonly WATER_CHARACTER: string;
  readonly SAND_CHARACTER: string;
  readonly GRASS_CHARACTER: string;
  readonly MOUNTAIN_CHARACTER: string;
  readonly IRON_NODE_CHARACTER: string;
  readonly COPPER_NODE_CHARACTER: string;
  readonly BRIDGE_CHARACTER: string;
  readonly VIEWPORT_WIDTH: number;
  readonly VIEWPORT_HEIGHT: number;
  readonly WORLD_WIDTH: number;
  readonly WORLD_HEIGHT: number;
  readonly WORLD_SEED: number;
}

export interface BiomeConfig {
  readonly name: string;
  readonly treeRate: number;
  readonly rockRate: number;
  readonly waterRate: number;
  readonly grassRate?: number;
  readonly sandRate?: number;
  readonly mountainRate?: number;
  readonly ironRate: number;
  readonly copperRate: number;
  readonly terrainChar: string;
}

export interface BiomesConfig {
  readonly FOREST: BiomeConfig;
  readonly DESERT: BiomeConfig;
  readonly PLAINS: BiomeConfig;
  readonly MOUNTAINS: BiomeConfig;
  readonly LAKE: BiomeConfig;
  readonly TUNDRA: BiomeConfig;
  readonly BADLANDS: BiomeConfig;
  readonly WETLANDS: BiomeConfig;
}

export type BuildingCategory = 'mining' | 'production' | 'logistics' | 'power';

export type BuildMenuState = false | true | 'autoBuildOnSelect';

// Building type enum for type safety
export enum BuildingType {
  // Mining buildings
  MINER = 'miner',
  DRILL = 'drill',
  PRECISION_DRILL = 'precision_drill',
  SAND_DREDGER = 'sand_dredger',

  // Production buildings
  ASSEMBLER = 'assembler',
  FACTORY = 'factory',
  RESEARCH_FACILITY = 'research_facility',
  ROCKET_SILO = 'rocket_silo',

  // Logistics buildings
  BELT = 'belt',
  FAST_BELT = 'fast_belt',
  UNDERGROUND_BELT = 'underground_belt',
  SPLITTER = 'splitter',
  INSERTER = 'inserter',
  IRON_CHEST = 'iron_chest',
  STEEL_CHEST = 'steel_chest',
  WAREHOUSE = 'warehouse',

  // Power buildings
  GENERATOR = 'generator',
  SOLAR_PANEL = 'solar',
  WIND_TURBINE = 'wind_turbine',
}

// Specific building type unions for better type safety
export type BeltType = BuildingType.BELT | BuildingType.FAST_BELT;
export type MiningBuildingType =
  | BuildingType.MINER
  | BuildingType.DRILL
  | BuildingType.PRECISION_DRILL
  | BuildingType.SAND_DREDGER;
export type ProductionBuildingType =
  | BuildingType.ASSEMBLER
  | BuildingType.FACTORY
  | BuildingType.RESEARCH_FACILITY
  | BuildingType.ROCKET_SILO;
export type LogisticsBuildingType =
  | BuildingType.BELT
  | BuildingType.FAST_BELT
  | BuildingType.UNDERGROUND_BELT
  | BuildingType.SPLITTER
  | BuildingType.INSERTER
  | BuildingType.IRON_CHEST
  | BuildingType.STEEL_CHEST
  | BuildingType.WAREHOUSE;
export type PowerBuildingType =
  | BuildingType.GENERATOR
  | BuildingType.SOLAR_PANEL
  | BuildingType.WIND_TURBINE;

export type PlayerFacing = 'up' | 'down' | 'left' | 'right';

export const ASSET_PATHS = {
  ASSEMBLER_MODEL: '/assets/models/assembler.glb',
  CONVEYOR_MODEL: '/assets/conveyor.glb',
  GEAR_MODEL: '/assets/models/gear.glb',
  FACTORY_MODEL: '/assets/models/factory.glb',
  BASIC_DRILL_MODEL: '/assets/models/basic_drill.glb',
  ADVANCED_DRILL_MODEL: '/assets/models/advanced_drill.glb',
  SAND_MINER_MODEL: '/assets/models/sand_miner.glb',
  GENERATOR_MODEL: '/assets/models/generator.glb',
  SOLAR_PANEL_MODEL: '/assets/models/solar_panel.glb',
  RESEARCH_STATION_MODEL: '/assets/models/research_station.glb',
  SPLITTER_MODEL: '/assets/models/splitter.glb',
  UNDERGROUND_BELT_MODEL: '/assets/models/underground_belt.glb',
  IRON_CHEST_MODEL: '/assets/models/iron_chest.glb',
  STEEL_CHEST_MODEL: '/assets/models/steel_chest.glb',
  ROCKET_SILO_MODEL: '/assets/models/rocket_silo.glb',
  IRON_DEPOSIT_MODEL: '/assets/models/iron_deposit.glb',
  COPPER_DEPOSIT_MODEL: '/assets/models/copper_deposit.glb',
  IRON_ORE_MODEL: '/assets/models/iron_ore.glb',
  COPPER_ORE_MODEL: '/assets/models/copper_ore.glb',
  IRON_PLATE_MODEL: '/assets/models/iron_plate.glb',
  COPPER_PLATE_MODEL: '/assets/models/copper_plate.glb',
  BASIC_CIRCUIT_MODEL: '/assets/models/basic_circuit.glb',
  ADVANCED_CIRCUIT_MODEL: '/assets/models/advanced_circuit.glb',
  TRANSISTOR_MODEL: '/assets/models/transistor.glb',
  PLAYER_MODEL: '/assets/models/player.glb',
  SAND_MODEL: '/assets/models/sand.glb',
} as const;

export const SOUND_PATHS = {
  MINER_WORK: '/audio/sfx/miner.mp3',
  ADVANCED_DRILL_WORK: '/audio/sfx/miner.mp3',
  PRECISION_DRILL_WORK: '/audio/sfx/miner.mp3',
  SAND_DREDGER_WORK: '/audio/sfx/miner.mp3',
  ASSEMBLER_WORK: '/audio/sfx/assembler.mp3',
  FACTORY_WORK: '/audio/sfx/factory.mp3',
  RESEARCH_FACILITY_WORK: '/audio/sfx/research_station.mp3',
  ROCKET_SILO_WORK: '/audio/rocket_silo_work.mp3',
  BELT_LOOP: '/audio/sfx/belt_hum.mp3',
  FAST_BELT_LOOP: '/audio/sfx/belt_hum.mp3',
  UNDERGROUND_BELT_LOOP: '/audio/sfx/belt_hum.mp3',
  SPLITTER_ACTIVE: '/audio/splitter_active.mp3',
  INSERTER_ACTIVE: '/audio/inserter_active.mp3',
  GENERATOR_HUM: '/audio/generator_hum.mp3',
  SOLAR_PANEL_HUM: '/audio/solar_panel_hum.mp3',
  STEP: '/audio/sfx/step.mp3',
  ITEM_COMPLETE_THUD: '/audio/sfx/item_complete_thud.mp3',
} as const;

// Item types for belt transport system
export type ItemType =
  | 'iron-ore'
  | 'copper-ore'
  | 'iron-plate'
  | 'copper-plate'
  | 'gear'
  | 'circuit'
  | 'advanced-circuit'
  | 'sand'
  | 'transistor'
  | 'steel-chest';

export const ITEM_TYPES: readonly ItemType[] = [
  'iron-ore',
  'copper-ore',
  'iron-plate',
  'copper-plate',
  'gear',
  'circuit',
  'advanced-circuit',
  'sand',
  'transistor',
  'steel-chest',
] as const;

export const createEmptyItemCounts = (): Record<ItemType, number> =>
  ITEM_TYPES.reduce<Record<ItemType, number>>(
    (counts, itemType) => {
      counts[itemType] = 0;
      return counts;
    },
    {} as Record<ItemType, number>
  );

export interface Item {
  readonly type: ItemType;
  readonly character: string;
  readonly name: string;
  readonly model?: string;
}

export interface ItemsConfig {
  readonly IRON_ORE: Item;
  readonly COPPER_ORE: Item;
  readonly IRON_PLATE: Item;
  readonly COPPER_PLATE: Item;
  readonly GEAR: Item;
  readonly CIRCUIT: Item;
  readonly ADVANCED_CIRCUIT: Item;
  readonly SAND: Item;
  readonly TRANSISTOR: Item;
  readonly STEEL_CHEST: Item;
}

// Recipe system for assemblers
export interface RecipeIngredient {
  readonly item: ItemType;
  readonly count: number;
}

export interface Recipe {
  readonly id: string;
  readonly name: string;
  readonly inputs: RecipeIngredient[];
  readonly outputs: RecipeIngredient[];
  readonly craftingTime: number; // seconds
  readonly craftingBuilding: ProductionBuildingType;
}

export interface RecipesConfig {
  readonly IRON_PLATE: Recipe;
  readonly COPPER_PLATE: Recipe;
  readonly GEAR: Recipe;
  readonly CIRCUIT: Recipe;
  readonly ADVANCED_CIRCUIT: Recipe;
  readonly TRANSISTOR: Recipe;
  readonly STEEL_CHEST: Recipe;
}

export const RESEARCH_IDS = {
  IRON_STORAGE: 'iron-storage',
  STEEL_STORAGE: 'steel-storage',
  BASIC_CIRCUITS: 'basic-circuits',
  ADVANCED_FACTORIES: 'advanced-factories',
  ADVANCED_MINING_DRILL: 'advanced-mining-drill',
  PRECISION_MINING_DRILL: 'precision-mining-drill',
  FAST_LOGISTICS: 'fast-logistics',
  MINER_OPTIMIZATION: 'miner-optimization',
  BELT_ACCELERATION: 'belt-acceleration',
  ASSEMBLER_OPTIMIZATION: 'assembler-optimization',
  FACTORY_OPTIMIZATION: 'factory-optimization',
  OUTPUT_BUFFERING: 'output-buffering',
  BELT_STACKING: 'belt-stacking',
  GENERATOR_OPTIMIZATION: 'generator-optimization',
  UNDERGROUND_BELT: 'underground-belt',
  SAND_DREDGING: 'sand-dredging',
  TRANSISTOR_FABRICATION: 'transistor-fabrication',
  ADVANCED_CIRCUITS: 'advanced-circuits',
  SOLAR_INFRASTRUCTURE: 'solar-infrastructure',
  WIND_TURBINES: 'wind-turbines',
  ROCKET_SILO: 'rocket-silo',
} as const;

export type ResearchId = (typeof RESEARCH_IDS)[keyof typeof RESEARCH_IDS];

export interface ResearchUnlocks {
  readonly buildings?: readonly BuildingType[];
  readonly recipes?: readonly Recipe['id'][];
}

export interface ResearchRequirement {
  readonly item: ItemType;
  readonly amount: number;
}

export type RepeatableResearchType =
  | 'belt-speed'
  | 'assembler-speed'
  | 'factory-speed'
  | 'miner-speed'
  | 'output-capacity'
  | 'belt-stacking'
  | 'generator-output';

export interface RepeatableResearchConfig {
  readonly type: RepeatableResearchType;
  readonly bonusPerLevel: number;
  readonly costGrowth?: number;
  readonly maxLevel?: number;
}

export interface Research {
  readonly id: ResearchId;
  readonly name: string;
  readonly description: string;
  readonly unlocks: ResearchUnlocks;
  readonly prerequisites: readonly ResearchId[];
  readonly requirements?: readonly ResearchRequirement[];
  readonly repeatable?: RepeatableResearchConfig;
}

export type ResearchConfig = Record<ResearchId, Research>;

export const RESEARCHES: ResearchConfig = {
  [RESEARCH_IDS.IRON_STORAGE]: {
    id: RESEARCH_IDS.IRON_STORAGE,
    name: 'Iron Storage',
    description: 'Unlocks improved storage solutions using iron chests.',
    unlocks: {
      buildings: [BuildingType.IRON_CHEST] as const,
    },
    prerequisites: [] as const,
    requirements: [
      {
        item: 'gear',
        amount: 25,
      },
    ] as const,
  },
  [RESEARCH_IDS.STEEL_STORAGE]: {
    id: RESEARCH_IDS.STEEL_STORAGE,
    name: 'Steel Storage',
    description: 'Unlocks high-capacity steel chests for bulk logistics.',
    unlocks: {
      buildings: [BuildingType.STEEL_CHEST, BuildingType.WAREHOUSE] as const,
      recipes: ['steel-chest'] as const,
    },
    prerequisites: [
      RESEARCH_IDS.IRON_STORAGE,
      RESEARCH_IDS.ADVANCED_FACTORIES,
    ] as const,
    requirements: [
      {
        item: 'gear',
        amount: 120,
      },
      {
        item: 'circuit',
        amount: 45,
      },
    ] as const,
  },
  [RESEARCH_IDS.BASIC_CIRCUITS]: {
    id: RESEARCH_IDS.BASIC_CIRCUITS,
    name: 'Basic Circuits',
    description: 'Enables the production of electronic circuits.',
    unlocks: {
      recipes: ['circuit'] as const,
    },
    prerequisites: [] as const,
    requirements: [
      {
        item: 'gear',
        amount: 30,
      },
    ] as const,
  },
  [RESEARCH_IDS.ADVANCED_FACTORIES]: {
    id: RESEARCH_IDS.ADVANCED_FACTORIES,
    name: 'Advanced Factories',
    description: 'Unlocks large-scale manufacturing facilities.',
    unlocks: {
      buildings: [BuildingType.FACTORY] as const,
    },
    prerequisites: [RESEARCH_IDS.BASIC_CIRCUITS] as const,
    requirements: [
      {
        item: 'circuit',
        amount: 60,
      },
    ] as const,
  },
  [RESEARCH_IDS.ADVANCED_MINING_DRILL]: {
    id: RESEARCH_IDS.ADVANCED_MINING_DRILL,
    name: 'Advanced Mining Drill',
    description: 'Unlocks the high-speed advanced mining drill.',
    unlocks: {
      buildings: [BuildingType.DRILL] as const,
    },
    prerequisites: [RESEARCH_IDS.BASIC_CIRCUITS] as const,
    requirements: [
      {
        item: 'iron-plate',
        amount: 60,
      },
      {
        item: 'gear',
        amount: 40,
      },
      {
        item: 'circuit',
        amount: 30,
      },
    ] as const,
  },
  [RESEARCH_IDS.SOLAR_INFRASTRUCTURE]: {
    id: RESEARCH_IDS.SOLAR_INFRASTRUCTURE,
    name: 'Solar Infrastructure',
    description:
      'Build dedicated solar farms to power massive late-game facilities.',
    unlocks: {
      buildings: [BuildingType.SOLAR_PANEL] as const,
    },
    prerequisites: [
      RESEARCH_IDS.TRANSISTOR_FABRICATION,
      RESEARCH_IDS.ADVANCED_FACTORIES,
    ] as const,
    requirements: [
      {
        item: 'transistor',
        amount: 120,
      },
      {
        item: 'circuit',
        amount: 200,
      },
    ] as const,
  },
  [RESEARCH_IDS.WIND_TURBINES]: {
    id: RESEARCH_IDS.WIND_TURBINES,
    name: 'Wind Turbines',
    description:
      'Unlocks wind turbines that provide clean but gusty power generation.',
    unlocks: {
      buildings: [BuildingType.WIND_TURBINE] as const,
    },
    prerequisites: [RESEARCH_IDS.ADVANCED_FACTORIES] as const,
    requirements: [
      {
        item: 'gear',
        amount: 80,
      },
      {
        item: 'circuit',
        amount: 60,
      },
    ] as const,
  },
  [RESEARCH_IDS.ROCKET_SILO]: {
    id: RESEARCH_IDS.ROCKET_SILO,
    name: 'Rocketry',
    description: 'Unlocks the rocket silo for interstellar launches.',
    unlocks: {
      buildings: [BuildingType.ROCKET_SILO] as const,
    },
    prerequisites: [
      RESEARCH_IDS.ADVANCED_FACTORIES,
      RESEARCH_IDS.ADVANCED_CIRCUITS,
      RESEARCH_IDS.SOLAR_INFRASTRUCTURE,
    ] as const,
    requirements: [
      {
        item: 'circuit',
        amount: 1000,
      },
      {
        item: 'gear',
        amount: 1000,
      },
    ] as const,
  },
  [RESEARCH_IDS.FAST_LOGISTICS]: {
    id: RESEARCH_IDS.FAST_LOGISTICS,
    name: 'Fast Logistics',
    description: 'Improves item transport speed with fast belts.',
    unlocks: {
      buildings: [BuildingType.FAST_BELT] as const,
    },
    prerequisites: [RESEARCH_IDS.IRON_STORAGE] as const,
    requirements: [
      {
        item: 'gear',
        amount: 90,
      },
      {
        item: 'iron-plate',
        amount: 120,
      },
    ] as const,
  },
  [RESEARCH_IDS.UNDERGROUND_BELT]: {
    id: RESEARCH_IDS.UNDERGROUND_BELT,
    name: 'Underground Belt',
    description: 'Lets belts travel underground for up to 6 tiles.',
    unlocks: {
      buildings: [BuildingType.UNDERGROUND_BELT] as const,
    },
    prerequisites: [RESEARCH_IDS.BASIC_CIRCUITS] as const,
    requirements: [
      {
        item: 'circuit',
        amount: 60,
      },
    ] as const,
  },
  [RESEARCH_IDS.SAND_DREDGING]: {
    id: RESEARCH_IDS.SAND_DREDGING,
    name: 'Sand Dredging',
    description:
      'Unlocks autonomous dredgers that extract sand without resource nodes.',
    unlocks: {
      buildings: [BuildingType.SAND_DREDGER] as const,
    },
    prerequisites: [RESEARCH_IDS.BASIC_CIRCUITS] as const,
    requirements: [
      {
        item: 'gear',
        amount: 60,
      },
      {
        item: 'iron-plate',
        amount: 40,
      },
    ] as const,
  },
  [RESEARCH_IDS.TRANSISTOR_FABRICATION]: {
    id: RESEARCH_IDS.TRANSISTOR_FABRICATION,
    name: 'Transistor Fabrication',
    description: 'Unlocks transistor production refined from dredged sand.',
    unlocks: {
      recipes: ['transistor'] as const,
    },
    prerequisites: [
      RESEARCH_IDS.SAND_DREDGING,
      RESEARCH_IDS.BASIC_CIRCUITS,
    ] as const,
    requirements: [
      {
        item: 'sand',
        amount: 120,
      },
      {
        item: 'circuit',
        amount: 30,
      },
    ] as const,
  },
  [RESEARCH_IDS.ADVANCED_CIRCUITS]: {
    id: RESEARCH_IDS.ADVANCED_CIRCUITS,
    name: 'Advanced Circuits',
    description:
      'Unlocks advanced circuit integration for late-game technologies.',
    unlocks: {
      recipes: ['advanced-circuit'] as const,
    },
    prerequisites: [
      RESEARCH_IDS.TRANSISTOR_FABRICATION,
      RESEARCH_IDS.ADVANCED_FACTORIES,
    ] as const,
    requirements: [
      {
        item: 'transistor',
        amount: 2,
      },
      {
        item: 'circuit',
        amount: 2,
      },
    ] as const,
  },
  [RESEARCH_IDS.PRECISION_MINING_DRILL]: {
    id: RESEARCH_IDS.PRECISION_MINING_DRILL,
    name: 'Precision Mining Drill',
    description:
      'Unlocks a mining drill that buffers multiple ores between belt drops.',
    unlocks: {
      buildings: [BuildingType.PRECISION_DRILL] as const,
    },
    prerequisites: [
      RESEARCH_IDS.ADVANCED_CIRCUITS,
      RESEARCH_IDS.ADVANCED_MINING_DRILL,
    ] as const,
    requirements: [
      {
        item: 'advanced-circuit',
        amount: 12,
      },
      {
        item: 'circuit',
        amount: 40,
      },
    ] as const,
  },
  [RESEARCH_IDS.MINER_OPTIMIZATION]: {
    id: RESEARCH_IDS.MINER_OPTIMIZATION,
    name: 'Miner Optimization',
    description:
      'Repeatable upgrades that increase miner extraction speed. +50% power usage per level.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.IRON_STORAGE] as const,
    requirements: [
      {
        item: 'gear',
        amount: 30,
      },
    ] as const,
    repeatable: {
      type: 'miner-speed',
      bonusPerLevel: 0.5,
      costGrowth: 1.4,
    },
  },
  [RESEARCH_IDS.BELT_ACCELERATION]: {
    id: RESEARCH_IDS.BELT_ACCELERATION,
    name: 'Belt Acceleration',
    description: 'Repeatable research that accelerates belt transport speed.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.FAST_LOGISTICS] as const,
    requirements: [
      {
        item: 'gear',
        amount: 40,
      },
      {
        item: 'circuit',
        amount: 20,
      },
    ] as const,
    repeatable: {
      type: 'belt-speed',
      bonusPerLevel: 0.1,
      costGrowth: 1.35,
    },
  },
  [RESEARCH_IDS.ASSEMBLER_OPTIMIZATION]: {
    id: RESEARCH_IDS.ASSEMBLER_OPTIMIZATION,
    name: 'Assembler Optimization',
    description:
      'Repeatable calibration that improves assembler crafting speed. +50% power usage per level.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.BASIC_CIRCUITS] as const,
    requirements: [
      {
        item: 'circuit',
        amount: 35,
      },
    ] as const,
    repeatable: {
      type: 'assembler-speed',
      bonusPerLevel: 0.5,
      costGrowth: 1.6,
    },
  },
  [RESEARCH_IDS.FACTORY_OPTIMIZATION]: {
    id: RESEARCH_IDS.FACTORY_OPTIMIZATION,
    name: 'Factory Optimization',
    description: 'Repeatable overclocks that boost factory throughput.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.ADVANCED_FACTORIES] as const,
    requirements: [
      {
        item: 'circuit',
        amount: 45,
      },
      {
        item: 'gear',
        amount: 25,
      },
    ] as const,
    repeatable: {
      type: 'factory-speed',
      bonusPerLevel: 0.08,
      costGrowth: 1.45,
    },
  },
  [RESEARCH_IDS.GENERATOR_OPTIMIZATION]: {
    id: RESEARCH_IDS.GENERATOR_OPTIMIZATION,
    name: 'Generator Efficiency',
    description: 'Repeatable tuning that increases generator power output.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.ADVANCED_FACTORIES] as const,
    requirements: [
      {
        item: 'iron-plate',
        amount: 80,
      },
      {
        item: 'gear',
        amount: 60,
      },
      {
        item: 'circuit',
        amount: 30,
      },
    ] as const,
    repeatable: {
      type: 'generator-output',
      bonusPerLevel: 0.15,
      costGrowth: 1.5,
    },
  },
  [RESEARCH_IDS.OUTPUT_BUFFERING]: {
    id: RESEARCH_IDS.OUTPUT_BUFFERING,
    name: 'Output Buffering',
    description: 'Repeatable upgrade that doubles building output storage.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.ADVANCED_FACTORIES] as const,
    requirements: [
      {
        item: 'gear',
        amount: 60,
      },
      {
        item: 'circuit',
        amount: 60,
      },
    ] as const,
    repeatable: {
      type: 'output-capacity',
      bonusPerLevel: 1,
      costGrowth: 1.5,
    },
  },
  [RESEARCH_IDS.BELT_STACKING]: {
    id: RESEARCH_IDS.BELT_STACKING,
    name: 'Conveyor Belt Stacking',
    description:
      'Repeatable upgrade that doubles the item stacking capacity of belts.',
    unlocks: {},
    prerequisites: [RESEARCH_IDS.FAST_LOGISTICS] as const,
    requirements: [
      {
        item: 'gear',
        amount: 80,
      },
      {
        item: 'iron-plate',
        amount: 150,
      },
    ] as const,
    repeatable: {
      type: 'belt-stacking',
      bonusPerLevel: 1,
      costGrowth: 1.4,
    },
  },
} as const;

// TODO: Create a research menu to display available technologies to the player.

// Belt content for tracking items on belts
export interface BeltItem {
  readonly item: ItemType;
  readonly progress: number; // 0-1, position along the belt tile
  readonly id: string; // unique identifier for tracking
  readonly count?: number; // number of items carried in this stack
}

// Assembler inventory for tracking items in assemblers
export interface AssemblerInventory {
  readonly items: Record<ItemType, number>; // item type -> count
  readonly recipe: Recipe | null; // currently selected recipe
  readonly craftingProgress: number; // 0-1, crafting progress (0 = not crafting, 1 = complete)
  readonly craftingStartTick: number | null; // game tick when crafting started (null = not crafting)
}

export interface ChestInventory {
  readonly items: Record<ItemType, number>; // item type -> total item count in units
}

export interface BuildingItemRequirement {
  readonly item: ItemType;
  readonly amount: number;
}

export interface BuildingCost {
  readonly items?: readonly BuildingItemRequirement[];
}

export interface Building {
  readonly id: BuildingType;
  readonly name: string;
  readonly character: string;
  readonly description: string;
  readonly category: BuildingCategory;
  readonly cost: BuildingCost;
  readonly hotkey: string;
  readonly model?: string;
  readonly size?: { width: number; height: number }; // Optional size for multi-tile buildings (default 1x1)
  readonly isPlaceUnderPlayer?: boolean; // Optional flag to place under player
  readonly blocking: boolean; // Whether the building blocks player movement
  readonly maxInputOutputsMultiplier?: number; // How many recipe batches worth of IO this building can buffer
  readonly stackCapacity?: number; // Number of item stacks this building can store
  readonly showProgressBar?: boolean;
  readonly requiresResourceNode?: boolean;
  readonly miningSpeedMultiplier?: number; // Multiplier applied to base mining output speed (1 = base rate)
  readonly miningOutputPerCycle?: number; // Number of resource units produced per mining cycle
  readonly miningBufferCapacity?: number; // How many mined resources can be buffered internally
  readonly powerConsumption?: number; // Power demand in MW
  readonly powerGeneration?: number; // Power production in MW
  readonly powerGenerationVariance?: {
    readonly amplitude: number; // Peak deviation from base generation in MW
    readonly period: number; // Seconds per full fluctuation cycle
  };
  readonly workingSound?: string; // Optional path to the sound that plays while the building is active
}

export interface BuildingsConfig {
  readonly MINER: Building;
  readonly DRILL: Building;
  readonly PRECISION_DRILL: Building;
  readonly SAND_DREDGER: Building;
  readonly ASSEMBLER: Building;
  readonly FACTORY: Building;
  readonly RESEARCH_FACILITY: Building;
  readonly ROCKET_SILO: Building;
  readonly BELT: Building;
  readonly FAST_BELT: Building;
  readonly UNDERGROUND_BELT: Building;
  readonly SPLITTER: Building;
  readonly INSERTER: Building;
  readonly IRON_CHEST: Building;
  readonly STEEL_CHEST: Building;
  readonly WAREHOUSE: Building;
  readonly GENERATOR: Building;
  readonly SOLAR_PANEL: Building;
  readonly WIND_TURBINE: Building;
}

export interface CategoryInfo {
  readonly name: string;
  readonly color: string;
  readonly hotkey: string;
}

export interface BuildingCategoriesConfig {
  readonly mining: CategoryInfo;
  readonly production: CategoryInfo;
  readonly logistics: CategoryInfo;
  readonly power: CategoryInfo;
}

export interface ControlsConfig {
  readonly MOVE_UP: readonly string[];
  readonly MOVE_DOWN: readonly string[];
  readonly MOVE_LEFT: readonly string[];
  readonly MOVE_RIGHT: readonly string[];
  readonly BUILD_MENU: readonly string[];
  readonly TAKE_ACTION: readonly string[];
  readonly COLLECT_RESOURCE: readonly string[];
  readonly BUILD_DECONSTRUCT: readonly string[];
  readonly MULTIPLE_PLACEMENT: readonly string[]; // Hold to place multiple buildings
  readonly CANCEL: readonly string[];
  readonly ZOOM_IN: readonly string[];
  readonly ZOOM_OUT: readonly string[];
  readonly ZOOM_RESET: readonly string[];
  readonly CATEGORY_MINING: readonly string[];
  readonly CATEGORY_PRODUCTION: readonly string[];
  readonly CATEGORY_LOGISTICS: readonly string[];
  readonly CATEGORY_POWER: readonly string[];
  readonly INVENTORY: readonly string[];
  readonly SAVE_GAME: readonly string[];
  readonly SAVE_MENU: readonly string[];
  readonly DEBUG_MENU: readonly string[];
  readonly ACHIEVEMENT_MENU: readonly string[];
  readonly HELP_MENU: readonly string[];
  readonly RESEARCH_MENU: readonly string[];
  readonly PRODUCTION_MENU: readonly string[];
}

export interface SaveConfig {
  readonly SAVE_KEY: string;
  readonly VERSION: string;
  readonly AUTO_SAVE_INTERVAL: number;
  readonly SAVE_ON_UNLOAD: boolean;
}

// Game Constants
export const GAME_CONFIG: GameConfig = {
  PLAYER_CHARACTER: '🤖',
  WALL_CHARACTER: '#',
  EMPTY_CHARACTER: ' ',
  TREE_CHARACTER: 'T',
  ROCK_CHARACTER: 'O',
  WATER_CHARACTER: '~',
  SAND_CHARACTER: '.',
  GRASS_CHARACTER: '"',
  MOUNTAIN_CHARACTER: '^',
  IRON_NODE_CHARACTER: 'I',
  COPPER_NODE_CHARACTER: 'C',
  BRIDGE_CHARACTER: '=',

  // Viewport size (what player sees)
  VIEWPORT_WIDTH: 120,
  VIEWPORT_HEIGHT: 40,

  // World size (much larger)
  WORLD_WIDTH: 1000,
  WORLD_HEIGHT: 1000,

  // World generation seed
  WORLD_SEED: 12345,
} as const;

// Transport belt directional characters
export const BELT_CHARACTERS = {
  right: '→',
  left: '←',
  up: '↑',
  down: '↓',
} as const;

export const FAST_BELT_CHARACTERS = {
  right: '⇒',
  left: '⇐',
  up: '⇑',
  down: '⇓',
} as const;

// Biome definitions
export const BIOMES: BiomesConfig = {
  FOREST: {
    name: 'Forest',
    treeRate: 0.0, // Now handled by clustering
    rockRate: 0.0, // Now handled by clustering
    waterRate: 0.002,
    grassRate: 0.15,
    ironRate: 0.002, // Restored to reasonable level
    copperRate: 0.003, // Restored to reasonable level
    terrainChar: GAME_CONFIG.GRASS_CHARACTER,
  },
  DESERT: {
    name: 'Desert',
    treeRate: 0.0, // Now handled by clustering
    rockRate: 0.0, // Now handled by clustering
    waterRate: 0.0005,
    sandRate: 0.25,
    ironRate: 0.004, // Restored to reasonable level
    copperRate: 0.002, // Restored to reasonable level
    terrainChar: GAME_CONFIG.SAND_CHARACTER,
  },
  PLAINS: {
    name: 'Plains',
    treeRate: 0.0, // Now handled by clustering
    rockRate: 0.0, // Now handled by clustering
    waterRate: 0.003,
    grassRate: 0.18,
    ironRate: 0.003, // Restored to reasonable level
    copperRate: 0.004, // Restored to reasonable level
    terrainChar: GAME_CONFIG.GRASS_CHARACTER,
  },
  MOUNTAINS: {
    name: 'Mountains',
    treeRate: 0.0, // Now handled by clustering
    rockRate: 0.0, // Now handled by clustering
    waterRate: 0.002,
    mountainRate: 0.015,
    ironRate: 0.008, // Restored to reasonable level
    copperRate: 0.006, // Restored to reasonable level
    terrainChar: GAME_CONFIG.MOUNTAIN_CHARACTER,
  },
  LAKE: {
    name: 'Lake',
    treeRate: 0.0, // Now handled by clustering
    rockRate: 0.0, // Now handled by clustering
    waterRate: 0.12,
    grassRate: 0.1,
    ironRate: 0.001, // Restored to reasonable level
    copperRate: 0.002, // Restored to reasonable level
    terrainChar: GAME_CONFIG.WATER_CHARACTER,
  },
  TUNDRA: {
    name: 'Tundra',
    treeRate: 0.0,
    rockRate: 0.0,
    waterRate: 0.0015,
    grassRate: 0.09,
    mountainRate: 0.006,
    ironRate: 0.005,
    copperRate: 0.003,
    terrainChar: GAME_CONFIG.GRASS_CHARACTER,
  },
  BADLANDS: {
    name: 'Badlands',
    treeRate: 0.0,
    rockRate: 0.0,
    waterRate: 0.0008,
    sandRate: 0.18,
    mountainRate: 0.008,
    ironRate: 0.006,
    copperRate: 0.007,
    terrainChar: GAME_CONFIG.SAND_CHARACTER,
  },
  WETLANDS: {
    name: 'Wetlands',
    treeRate: 0.0,
    rockRate: 0.0,
    waterRate: 0.025,
    grassRate: 0.24,
    ironRate: 0.002,
    copperRate: 0.003,
    terrainChar: GAME_CONFIG.GRASS_CHARACTER,
  },
} as const;

// Building definitions
export const BUILDINGS: BuildingsConfig = {
  // Mining
  MINER: {
    id: BuildingType.MINER,
    name: 'Miner',
    character: '⚒',
    description: 'Extracts resources from the ground',
    category: 'mining',
    model: ASSET_PATHS.BASIC_DRILL_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 10 },
        { item: 'gear', amount: 5 },
      ],
    },
    hotkey: '1',
    blocking: true,
    requiresResourceNode: true,
    miningSpeedMultiplier: 1,
    powerConsumption: 5,
    workingSound: SOUND_PATHS.MINER_WORK,
  },
  DRILL: {
    id: BuildingType.DRILL,
    name: 'Advanced Drill',
    character: 'D',
    description: 'High-speed resource extraction',
    category: 'mining',
    model: ASSET_PATHS.ADVANCED_DRILL_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 20 },
        { item: 'gear', amount: 12 },
        { item: 'circuit', amount: 6 },
      ],
    },
    hotkey: '2',
    blocking: true,
    requiresResourceNode: true,
    miningSpeedMultiplier: 2,
    powerConsumption: 8,
    workingSound: SOUND_PATHS.ADVANCED_DRILL_WORK,
  },
  PRECISION_DRILL: {
    id: BuildingType.PRECISION_DRILL,
    name: 'Precision Drill',
    character: 'P',
    description:
      'Maintains base mining speed while buffering ore between drops',
    category: 'mining',
    model: ASSET_PATHS.ADVANCED_DRILL_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 25 },
        { item: 'gear', amount: 15 },
        { item: 'circuit', amount: 10 },
        { item: 'advanced-circuit', amount: 4 },
      ],
    },
    hotkey: '4',
    blocking: true,
    requiresResourceNode: true,
    miningSpeedMultiplier: 1,
    miningOutputPerCycle: 1,
    miningBufferCapacity: 4,
    powerConsumption: 6,
    workingSound: SOUND_PATHS.PRECISION_DRILL_WORK,
  },
  SAND_DREDGER: {
    id: BuildingType.SAND_DREDGER,
    name: 'Sand Dredger',
    character: '≈',
    description: 'Continuously dredges sand from loose terrain',
    category: 'mining',
    model: ASSET_PATHS.SAND_MINER_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 18 },
        { item: 'gear', amount: 10 },
      ],
    },
    hotkey: '3',
    blocking: true,
    requiresResourceNode: false,
    miningSpeedMultiplier: 1,
    powerConsumption: 4,
    workingSound: SOUND_PATHS.SAND_DREDGER_WORK,
  },

  // Production
  ASSEMBLER: {
    id: BuildingType.ASSEMBLER,
    name: 'Assembler',
    character: 'A',
    description: 'Crafts items from raw materials (3x3)',
    category: 'production',
    model: ASSET_PATHS.ASSEMBLER_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 20 },
        { item: 'gear', amount: 12 },
      ],
    },
    hotkey: '1',
    size: { width: 3, height: 3 },
    blocking: true,
    maxInputOutputsMultiplier: 2,
    powerConsumption: 12,
    workingSound: SOUND_PATHS.ASSEMBLER_WORK,
  },
  FACTORY: {
    id: BuildingType.FACTORY,
    name: 'Factory',
    character: 'F',
    description: 'Advanced manufacturing facility (3x3)',
    category: 'production',
    model: ASSET_PATHS.FACTORY_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 35 },
        { item: 'gear', amount: 20 },
        { item: 'circuit', amount: 20 },
      ],
    },
    hotkey: '2',
    size: { width: 3, height: 3 },
    blocking: true,
    maxInputOutputsMultiplier: 2,
    powerConsumption: 20,
    workingSound: SOUND_PATHS.FACTORY_WORK,
  },
  RESEARCH_FACILITY: {
    id: BuildingType.RESEARCH_FACILITY,
    name: 'Research Facility',
    character: 'R',
    description: 'Consumes items to progress selected research (3x3)',
    category: 'production',
    model: ASSET_PATHS.RESEARCH_STATION_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 30 },
        { item: 'circuit', amount: 10 },
      ],
    },
    hotkey: '3',
    size: { width: 3, height: 3 },
    blocking: true,
    showProgressBar: true,
    powerConsumption: 25,
    workingSound: SOUND_PATHS.RESEARCH_FACILITY_WORK,
  },
  ROCKET_SILO: {
    id: BuildingType.ROCKET_SILO,
    name: 'Rocket Silo',
    character: '🚀',
    description: 'Launch complex for endgame rockets (5x5)',
    category: 'production',
    model: ASSET_PATHS.ROCKET_SILO_MODEL,
    cost: {
      items: [
        { item: 'circuit', amount: 1000 },
        { item: 'gear', amount: 1000 },
        { item: 'advanced-circuit', amount: 500 },
      ] as const,
    },
    hotkey: '4',
    size: { width: 5, height: 5 },
    blocking: true,
    powerConsumption: 80,
    workingSound: SOUND_PATHS.ROCKET_SILO_WORK,
  },

  // Logistics
  BELT: {
    id: BuildingType.BELT,
    name: 'Transport Belt',
    character: '→',
    description: 'Moves items along a path',
    category: 'logistics',
    model: ASSET_PATHS.CONVEYOR_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 1 },
        { item: 'gear', amount: 1 },
      ],
    },
    hotkey: '1',
    isPlaceUnderPlayer: true,
    blocking: false,
    powerConsumption: 0.2,
    workingSound: SOUND_PATHS.BELT_LOOP,
  },
  FAST_BELT: {
    id: BuildingType.FAST_BELT,
    name: 'Fast Belt',
    character: '⇒',
    description: 'High-speed item transport',
    category: 'logistics',
    model: ASSET_PATHS.CONVEYOR_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 1 },
        { item: 'gear', amount: 2 },
        { item: 'circuit', amount: 1 },
      ],
    },
    hotkey: '2',
    isPlaceUnderPlayer: true,
    blocking: false,
    powerConsumption: 0.4,
    workingSound: SOUND_PATHS.FAST_BELT_LOOP,
  },
  UNDERGROUND_BELT: {
    id: BuildingType.UNDERGROUND_BELT,
    name: 'Underground Belt',
    character: '⛓',
    description: 'Transfers belt items underground up to 6 tiles.',
    category: 'logistics',
    model: ASSET_PATHS.UNDERGROUND_BELT_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 10 },
        { item: 'gear', amount: 5 },
        { item: 'circuit', amount: 2 },
      ],
    },
    hotkey: '7',
    isPlaceUnderPlayer: true,
    blocking: false,
    powerConsumption: 0.2,
    workingSound: SOUND_PATHS.UNDERGROUND_BELT_LOOP,
  },
  SPLITTER: {
    id: BuildingType.SPLITTER,
    name: 'Splitter',
    character: '+',
    description: 'Distributes items to adjacent belts',
    category: 'logistics',
    model: ASSET_PATHS.SPLITTER_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 12 },
        { item: 'gear', amount: 8 },
        { item: 'circuit', amount: 4 },
      ],
    },
    hotkey: '6',
    isPlaceUnderPlayer: true,
    blocking: false,
    powerConsumption: 1,
    workingSound: SOUND_PATHS.SPLITTER_ACTIVE,
  },
  INSERTER: {
    id: BuildingType.INSERTER,
    name: 'Inserter',
    character: '↺',
    description: 'Moves items between structures',
    category: 'logistics',
    cost: {
      items: [
        { item: 'iron-plate', amount: 2 },
        { item: 'gear', amount: 2 },
        { item: 'circuit', amount: 1 },
      ],
    },
    hotkey: '3',
    blocking: true,
    powerConsumption: 2,
    workingSound: SOUND_PATHS.INSERTER_ACTIVE,
  },
  IRON_CHEST: {
    id: BuildingType.IRON_CHEST,
    name: 'Iron Chest',
    character: '☐',
    description: 'Stores up to 8 stacks of items',
    category: 'logistics',
    model: ASSET_PATHS.IRON_CHEST_MODEL,
    cost: {
      items: [{ item: 'iron-plate', amount: 8 }],
    },
    hotkey: '4',
    blocking: true,
    stackCapacity: 8,
  },
  STEEL_CHEST: {
    id: BuildingType.STEEL_CHEST,
    name: 'Steel Chest',
    character: '☒',
    description: 'Stores up to 32 stacks of items',
    category: 'logistics',
    model: ASSET_PATHS.STEEL_CHEST_MODEL,
    cost: {
      items: [{ item: 'steel-chest', amount: 1 }] as const,
    },
    hotkey: '5',
    blocking: true,
    stackCapacity: 32,
  },
  WAREHOUSE: {
    id: BuildingType.WAREHOUSE,
    name: 'Warehouse',
    character: '▣',
    description: 'Stores up to 64 stacks with remote loading docks (2x2)',
    category: 'logistics',
    model: ASSET_PATHS.STEEL_CHEST_MODEL,
    cost: {
      items: [
        { item: 'steel-chest', amount: 2 },
        { item: 'gear', amount: 30 },
        { item: 'circuit', amount: 15 },
      ] as const,
    },
    hotkey: '8',
    blocking: true,
    size: { width: 2, height: 2 },
    stackCapacity: 64,
  },

  // Power
  GENERATOR: {
    id: BuildingType.GENERATOR,
    name: 'Generator',
    character: 'G',
    description: 'Produces electrical power',
    category: 'power',
    model: ASSET_PATHS.GENERATOR_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 20 },
        { item: 'gear', amount: 10 },
      ],
    },
    hotkey: '1',
    blocking: true,
    powerGeneration: 30,
    workingSound: SOUND_PATHS.GENERATOR_HUM,
  },
  WIND_TURBINE: {
    id: BuildingType.WIND_TURBINE,
    name: 'Wind Turbine',
    character: '🌀',
    description: 'Generates clean power that fluctuates with the wind',
    category: 'power',
    model: ASSET_PATHS.GENERATOR_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 18 },
        { item: 'gear', amount: 14 },
        { item: 'circuit', amount: 8 },
      ],
    },
    hotkey: '3',
    blocking: true,
    powerGeneration: 18,
    powerGenerationVariance: {
      amplitude: 6,
      period: 12,
    },
    workingSound: SOUND_PATHS.GENERATOR_HUM,
  },
  SOLAR_PANEL: {
    id: BuildingType.SOLAR_PANEL,
    name: 'Solar Panel',
    character: 'S',
    description: 'Generates power from sunlight',
    category: 'power',
    model: ASSET_PATHS.SOLAR_PANEL_MODEL,
    cost: {
      items: [
        { item: 'iron-plate', amount: 10 },
        { item: 'circuit', amount: 10 },
        { item: 'transistor', amount: 10 },
      ],
    },
    hotkey: '2',
    blocking: true,
    powerGeneration: 12,
    workingSound: SOUND_PATHS.SOLAR_PANEL_HUM,
  },
} as const;

export const BUILDINGS_BY_ID: Record<BuildingType, Building> = Object.values(
  BUILDINGS
).reduce(
  (acc, building) => {
    acc[building.id] = building;
    return acc;
  },
  {} as Record<BuildingType, Building>
);

// Item definitions for belt transport system
export const ITEMS: ItemsConfig = {
  IRON_ORE: {
    type: 'iron-ore',
    name: 'Iron Ore',
    character: 'i',
    model: ASSET_PATHS.IRON_ORE_MODEL,
  },
  COPPER_ORE: {
    type: 'copper-ore',
    name: 'Copper Ore',
    character: 'c',
    model: ASSET_PATHS.COPPER_ORE_MODEL,
  },
  IRON_PLATE: {
    type: 'iron-plate',
    name: 'Iron Plate',
    character: 'I',
    model: ASSET_PATHS.IRON_PLATE_MODEL,
  },
  COPPER_PLATE: {
    type: 'copper-plate',
    name: 'Copper Plate',
    character: 'C',
    model: ASSET_PATHS.COPPER_PLATE_MODEL,
  },
  GEAR: {
    type: 'gear',
    name: 'Gear',
    character: 'G',
    model: ASSET_PATHS.GEAR_MODEL,
  },
  CIRCUIT: {
    type: 'circuit',
    name: 'Circuit',
    character: '◯',
    model: ASSET_PATHS.BASIC_CIRCUIT_MODEL,
  },
  ADVANCED_CIRCUIT: {
    type: 'advanced-circuit',
    name: 'Advanced Circuit',
    character: 'Δ',
    model: ASSET_PATHS.ADVANCED_CIRCUIT_MODEL,
  },
  SAND: {
    type: 'sand',
    name: 'Sand',
    character: 's',
    model: ASSET_PATHS.SAND_MODEL,
  },
  TRANSISTOR: {
    type: 'transistor',
    name: 'Transistor',
    character: 'τ',
    model: ASSET_PATHS.TRANSISTOR_MODEL,
  },
  STEEL_CHEST: {
    type: 'steel-chest',
    name: 'Steel Chest',
    character: '☒',
    model: ASSET_PATHS.STEEL_CHEST_MODEL,
  },
} as const;

export const DEFAULT_ITEM_STACK_SIZE = 50;

export const ITEM_STACK_SIZES: Record<ItemType, number> = {
  'iron-ore': DEFAULT_ITEM_STACK_SIZE,
  'copper-ore': DEFAULT_ITEM_STACK_SIZE,
  'iron-plate': DEFAULT_ITEM_STACK_SIZE,
  'copper-plate': DEFAULT_ITEM_STACK_SIZE,
  gear: DEFAULT_ITEM_STACK_SIZE,
  circuit: DEFAULT_ITEM_STACK_SIZE,
  'advanced-circuit': DEFAULT_ITEM_STACK_SIZE,
  sand: DEFAULT_ITEM_STACK_SIZE,
  transistor: DEFAULT_ITEM_STACK_SIZE,
  'steel-chest': 10,
} as const;

export const getItemStackSize = (item: ItemType): number =>
  ITEM_STACK_SIZES[item] ?? DEFAULT_ITEM_STACK_SIZE;

export const STARTING_PLAYER_ITEMS: ReadonlyArray<{
  readonly itemType: ItemType;
  readonly count: number;
}> = [
  { itemType: 'iron-plate', count: ITEM_STACK_SIZES['iron-plate'] },
  { itemType: 'gear', count: ITEM_STACK_SIZES.gear },
  { itemType: 'circuit', count: ITEM_STACK_SIZES.circuit },
];

export const PLAYER_INVENTORY_SLOT_COUNT = 20;

// Resource node configuration
export interface ResourceNodeConfig {
  readonly character: string;
  readonly name: string;
  readonly isSolid: boolean; // Whether this resource blocks player movement
  readonly canMine: boolean; // Whether miners can be placed on this resource
}

export interface ResourceNodesConfig {
  readonly IRON_NODE: ResourceNodeConfig;
  readonly COPPER_NODE: ResourceNodeConfig;
}

export const RESOURCE_NODES: ResourceNodesConfig = {
  IRON_NODE: {
    character: GAME_CONFIG.IRON_NODE_CHARACTER,
    name: 'Iron Node',
    isSolid: true, // Blocks movement - requires mining
    canMine: true,
  },
  COPPER_NODE: {
    character: GAME_CONFIG.COPPER_NODE_CHARACTER,
    name: 'Copper Node',
    isSolid: true, // Blocks movement - requires mining
    canMine: true,
  },
} as const;

// Resource node character map for easy lookup
export const RESOURCE_NODE_MAP: Record<string, ResourceNodeConfig> = {
  [GAME_CONFIG.IRON_NODE_CHARACTER]: RESOURCE_NODES.IRON_NODE,
  [GAME_CONFIG.COPPER_NODE_CHARACTER]: RESOURCE_NODES.COPPER_NODE,
} as const;

// Recipe definitions for assemblers
export const RECIPES: RecipesConfig = {
  IRON_PLATE: {
    id: 'iron-plate',
    name: 'Iron Plate',
    craftingBuilding: BuildingType.ASSEMBLER,
    inputs: [{ item: 'iron-ore', count: 1 }],
    outputs: [{ item: 'iron-plate', count: 1 }],
    craftingTime: 3.2,
  },
  COPPER_PLATE: {
    id: 'copper-plate',
    name: 'Copper Plate',
    craftingBuilding: BuildingType.ASSEMBLER,
    inputs: [{ item: 'copper-ore', count: 1 }],
    outputs: [{ item: 'copper-plate', count: 1 }],
    craftingTime: 3.2,
  },
  GEAR: {
    id: 'gear',
    name: 'Gear',
    craftingBuilding: BuildingType.ASSEMBLER,
    inputs: [{ item: 'iron-plate', count: 2 }],
    outputs: [{ item: 'gear', count: 1 }],
    craftingTime: 0.5,
  },
  CIRCUIT: {
    id: 'circuit',
    name: 'Circuit',
    craftingBuilding: BuildingType.ASSEMBLER,
    inputs: [
      { item: 'copper-plate', count: 1 },
      { item: 'iron-plate', count: 1 },
    ],
    outputs: [{ item: 'circuit', count: 1 }],
    craftingTime: 0.5,
  },
  ADVANCED_CIRCUIT: {
    id: 'advanced-circuit',
    name: 'Advanced Circuit',
    craftingBuilding: BuildingType.FACTORY,
    inputs: [
      { item: 'transistor', count: 2 },
      { item: 'circuit', count: 2 },
    ],
    outputs: [{ item: 'advanced-circuit', count: 1 }],
    craftingTime: 1.8,
  },
  TRANSISTOR: {
    id: 'transistor',
    name: 'Transistor',
    craftingBuilding: BuildingType.ASSEMBLER,
    inputs: [{ item: 'sand', count: 3 }],
    outputs: [{ item: 'transistor', count: 1 }],
    craftingTime: 1.2,
  },
  STEEL_CHEST: {
    id: 'steel-chest',
    name: 'Steel Chest',
    craftingBuilding: BuildingType.FACTORY,
    inputs: [
      { item: 'iron-plate', count: 20 },
      { item: 'circuit', count: 8 },
    ],
    outputs: [{ item: 'steel-chest', count: 1 }],
    craftingTime: 6,
  },
} as const;

export const RECIPES_BY_ID: Readonly<Record<Recipe['id'], Recipe>> =
  Object.freeze(
    Object.values(RECIPES).reduce<Record<Recipe['id'], Recipe>>(
      (acc, recipe) => {
        acc[recipe.id] = recipe;
        return acc;
      },
      {} as Record<Recipe['id'], Recipe>
    )
  );

// Building categories for menu organization
export const BUILDING_CATEGORIES: BuildingCategoriesConfig = {
  mining: {
    name: 'Mining',
    color: '#ff6b35',
    hotkey: 'e',
  },
  production: {
    name: 'Production',
    color: '#4ecdc4',
    hotkey: 'r',
  },
  logistics: {
    name: 'Logistics',
    color: '#45b7d1',
    hotkey: 't',
  },
  power: {
    name: 'Power',
    color: '#f9ca24',
    hotkey: 'y',
  },
} as const;

export const formatHotkeyLabel = (keys: readonly string[]): string => {
  const normalizedKeys = keys.map(key => key.toUpperCase());
  return Array.from(new Set(normalizedKeys)).join(' / ');
};

export const CONTROLS: ControlsConfig = {
  MOVE_UP: ['ArrowUp', 'w'],
  MOVE_DOWN: ['ArrowDown', 's'],
  MOVE_LEFT: ['ArrowLeft', 'a'],
  MOVE_RIGHT: ['ArrowRight', 'd'],

  // Building controls
  BUILD_MENU: ['b'],
  TAKE_ACTION: [' '], // Contextual action - place building or open menu based on state
  COLLECT_RESOURCE: ['f', 'F'],
  BUILD_DECONSTRUCT: ['x', 'X'],
  MULTIPLE_PLACEMENT: ['Shift'], // Hold shift to place multiple buildings
  CANCEL: ['Escape'],

  // Zoom controls
  ZOOM_IN: ['+', '='],
  ZOOM_OUT: ['-', '_'],
  ZOOM_RESET: ['0'],

  // Category hotkeys
  CATEGORY_MINING: ['e'],
  CATEGORY_PRODUCTION: ['r'],
  CATEGORY_LOGISTICS: ['t'],
  CATEGORY_POWER: ['y'],

  // Inventory controls
  INVENTORY: ['i', 'I', 'e', 'E'],

  // Save controls
  SAVE_GAME: ['F5', 'S'], // Capital S means Shift+S
  SAVE_MENU: ['F1'], // Open save menu

  // Debug controls
  DEBUG_MENU: ['F10'], // Open debug menu

  // Achievement controls
  ACHIEVEMENT_MENU: ['F9'], // Open achievement menu

  // Help controls
  HELP_MENU: ['?'], // Open help menu
  // Research controls
  RESEARCH_MENU: ['F2'],
  // Production analytics controls
  PRODUCTION_MENU: ['p', 'P'],
} as const;

// Category number to hotkey mapping for tests - derived from CONTROLS to ensure consistency
export const CATEGORY_HOTKEY_MAP = {
  '1': CONTROLS.CATEGORY_MINING[0], // Mining
  '2': CONTROLS.CATEGORY_LOGISTICS[0], // Logistics
  '3': CONTROLS.CATEGORY_PRODUCTION[0], // Production
  '4': CONTROLS.CATEGORY_POWER[0], // Power
} as const;

// Save/Load configuration
export const SAVE_CONFIG: SaveConfig = {
  SAVE_KEY: 'automation_game_save',
  VERSION: '1.1', // Incremented for building layer architecture changes
  AUTO_SAVE_INTERVAL: 30000, // Auto-save every 30 seconds
  SAVE_ON_UNLOAD: true,
} as const;
